/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography2, typography2 } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/xdp';

type Size = 'small' | 'large';

type Props = {
  rating?: number | null;
  ratingCount?: number | null;
  size?: Size;
};

const styles = {
  container: css`
    min-width: 0;
    display: flex;
  `,
  rating: (size: Size) => css`
    display: inline-flex;
    align-items: center;
    padding-right: var(--cds-spacing-100);
    ${size === 'small'
      ? typography2.subtitleMedium
      : {
          ...typography2.subtitleLarge,
          lineHeight: 'var(--cds-line-height-title2)',
          letterSpacing: 'var(--cds-letter-spacing-125)',
        }};
    color: var(--cds-color-grey-975);
  `,
  starIcon: css`
    padding-left: var(--cds-spacing-50);
  `,
  ratingText: css`
    min-width: 0;
    display: flex;

    & > span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  `,
};

const Rating = ({ rating, ratingCount, size = 'small' }: Props) => {
  const iconSize = size === 'small' ? 'medium' : 'large';
  const fixedRating = rating?.toFixed(1);

  if (!rating) {
    return null;
  }

  return (
    <span css={styles.container}>
      <span aria-label={_t('#{fixedRating} stars', { fixedRating })} role="group" css={styles.rating(size)}>
        {fixedRating} <StarFilledIcon size={iconSize} color="interactive" css={styles.starIcon} />
      </span>
      {Boolean(ratingCount) && ratingCount && (
        <Typography2 variant="bodySecondary" component="span" color="supportText" css={styles.ratingText}>
          (
          <FormattedMessage
            message={_t('{ratingCount, plural, =1 {{ratingCount} rating} other {{ratingCount} ratings}}')}
            ratingCount={ratingCount.toLocaleString()}
          />
          )
        </Typography2>
      )}
    </span>
  );
};
export default Rating;
